import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/site/Reveal";

const fallback = [
  { year: "1840", title: "Apertura delle Antiche Cave Balma", body: "Inizio dell'estrazione della sienite nella Valle Cervo, gestita tramite concessioni." },
  { year: "1880", title: "Espansione dei picapere", body: "Le maestranze locali, i 'picapere', perfezionano le tecniche di taglio della pietra." },
  { year: "1920", title: "Età d'oro della sienite", body: "La sienite della Balma viene esportata per monumenti e architetture pubbliche." },
  { year: "1979", title: "Chiusura delle cave storiche", body: "Termina ufficialmente l'attività estrattiva intensiva nella zona della Balma." },
  { year: "2024", title: "Eredità contemporanea", body: "GMB Graniti s.r.l. continua l'estrazione e la lavorazione della sienite a Campiglia Cervo, ultima cava attiva della Valle Cervo." },
];

const HISTORY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/23c5664e-366b-4aed-89ee-66b59e602110/images/f8b6dd6f1b6bcc9bc080d942d2166949cfe7fbf49ccc02b96f8ad324645833ac.png";

export default function Storia() {
  const [items, setItems] = useState(fallback);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quarry/timeline`;
    axios
      .get(url)
      .then((r) => {
        if (r.data?.items?.length) setItems(r.data.items);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="storia" data-testid="storia-section" className="relative section-pad section-gap">
      {/* Top label */}
      <div className="grid md:grid-cols-12 gap-10 mb-20">
        <Reveal className="md:col-span-3">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-neutral-500 mb-3">
            01 / Storia
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-neutral-50 leading-[0.9]">
            1840
            <br />
            <span className="text-[var(--accent)]">—</span>
            <br />
            1979
          </h2>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-5" delay={0.15}>
          <p className="font-editorial italic text-neutral-300 text-2xl md:text-3xl leading-snug">
            «Per oltre centottant'anni i picapere hanno cavato la sienite della Balma —
            blocco dopo blocco, stagione dopo stagione — costruendo il volto in pietra di
            una valle.»
          </p>
          <p className="mt-8 text-neutral-400 max-w-xl leading-relaxed">
            Le concessioni della Balma a Campiglia Cervo furono per oltre un secolo il
            cuore pulsante dell'estrazione di sienite in Italia. Una pietra dura, dal
            grano cristallino, lavorata a mano e poi spedita in ogni angolo del Paese.
          </p>
        </Reveal>
      </div>

      {/* Editorial image + numbers */}
      <div className="grid md:grid-cols-12 gap-6 mb-20">
        <Reveal className="md:col-span-7 relative aspect-[16/10] overflow-hidden border border-[var(--border-subtle)]">
          <img
            src={HISTORY_IMG}
            alt="Cava storica della Balma, fine '800"
            className="w-full h-full object-cover grayscale-[20%]"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
            <p className="text-xs tracking-[0.3em] uppercase text-neutral-400">
              Archivio · Antiche Cave Balma
            </p>
          </div>
        </Reveal>
        <StaggerGroup className="md:col-span-5 grid grid-cols-2 gap-px bg-[var(--border-subtle)]">
          {[
            { k: "180+", l: "Anni di tradizione" },
            { k: "12+", l: "Cave storiche" },
            { k: "6,5km", l: "Da Biella a S. Paolo Cervo" },
            { k: "1100m", l: "Quota della cava" },
          ].map((s, i) => (
            <StaggerItem
              key={i}
              className="bg-[var(--surface-1)] p-6 md:p-8 hover-lift hover:border-[var(--accent)]"
            >
              <p className="font-display text-4xl md:text-5xl text-neutral-100">{s.k}</p>
              <p className="mt-3 text-xs tracking-[0.2em] uppercase text-neutral-500">
                {s.l}
              </p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>

      {/* Timeline */}
      <div data-testid="storia-timeline" className="relative">
        <div className="absolute left-0 right-0 top-7 h-px bg-[var(--border-subtle)] hidden md:block" />
        <StaggerGroup className="grid md:grid-cols-5 gap-6" stagger={0.12}>
          {items.map((it, i) => (
            <StaggerItem key={i} className="relative">
              <div className="hidden md:block absolute left-0 top-5 w-3 h-3 rotate-45 bg-[var(--accent)]" />
              <p className="font-display text-3xl md:text-4xl text-neutral-100 mb-3 mt-0 md:mt-12">
                {it.year}
              </p>
              <p className="text-sm font-medium text-neutral-300 mb-2">{it.title}</p>
              <p className="text-sm text-neutral-500 leading-relaxed">{it.body}</p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}
